import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WeatherDataItem, WeatherPropertyCodes, useWeatherData } from './hooks/useWeatherData';
import WeaterForcastWindDirection from './windDirection';
import useLinks from '../../hooks/useLinks';

export interface WeatherBlockProps {
  show: boolean;
}
export interface WeatherBlockImageProps {
  url: string | null;
  show?: boolean;
  onImageLoaded?: Function;
}

export interface WeatherBlockTextProps {
  label: string;
  body: string;
  iconUrl: string;
  iconAltText: string;
}

export interface WeaterForcastTemperatureProps {
  weatherData: WeatherDataItem[] | null;
}

export interface WeaterForcastDetailsProps {
  weatherData: WeatherDataItem[] | null;
}

// --------------------------------------
//  Background image
// --------------------------------------

const BackgroundImage = ({ url, onImageLoaded }: WeatherBlockImageProps) => {
  const [loaded, setLoaded] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (url) {
      setImageUrl(url);
    }
  }, [url]);

  useEffect(() => {
    if (imageUrl) {
      let image = new Image();
      image.src = imageUrl;

      image.onload = () => {
        setLoaded(true);
        if (onImageLoaded) {
          onImageLoaded();
        }
      };

      image.onerror = () => {};
    }
  }, [imageUrl]);

  return (
    <div className={`weather-background ${loaded ? 'show' : ''}`} style={{ backgroundImage: `url(${imageUrl})` }}></div>
  );
};

// --------------------------------------
//  ArrivalsDeparturesTeaser
// --------------------------------------

const WeatherBlock = ({ show }: WeatherBlockProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [locale, setLocale] = useState<string>('en');
  const [latLng, setLatLng] = useState<number[] | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string>('');
  const [refreshData, setRefreshData] = useState(false);
  const { t } = useTranslation();

  let observer: IntersectionObserver;

  // ----------------------------------------------------------------------------
  //  useWeatherData hook - load the weather data from SMHI
  // ----------------------------------------------------------------------------

  const { weatherDataItem } = useWeatherData(latLng);

  // ----------------------------------------------------------------------------
  //  link
  // ----------------------------------------------------------------------------

  const { url, target } = useLinks(linkUrl);

  // ----------------------------------------------------------------------------
  //  useEffect - Mount
  // ----------------------------------------------------------------------------

  useEffect(() => {
    const e = document.getElementById('weatherBlock');
    if (document.documentElement.lang) {
      setLocale(document.documentElement.lang);
    }

    if (e?.dataset.link) {
      setLinkUrl(e.dataset.link);
    }

    if (e?.dataset.location) {
      setLocationName(e.dataset.location);
    }

    const lat = !isNaN(Number(e?.dataset.lat)) ? Number(e?.dataset.lat) : 57.6842793446946;
    const lng = !isNaN(Number(e?.dataset.lng)) ? Number(e?.dataset.lng) : 11.85152760860382;

    if (lat && lng) {
      setLatLng([lat, lng]);
    }

    setRefreshData(true);
  }, []);

  // --------------------------------------
  //  WeaterForcastTemperature
  // --------------------------------------

  // 1	Clear sky             7 	Fog                       14	Heavy sleet showers     21	Thunder
  // 2	Nearly clear sky      8	  Light rain showers        15	Light snow showers      22	Light sleet
  // 3	Variable cloudiness   9	  Moderate rain showers     16	Moderate snow showers   23	Moderate sleet
  // 4	Halfclear sky         10	Heavy rain showers        17	Heavy snow showers      24	Heavy sleet
  // 5	Cloudy sky            11	Thunderstorm              18	Light rain              25	Light snowfall
  // 6	Overcast              12	Light sleet showers       19	Moderate rain           26	Moderate snowfall
  // 7	Fog                   13	Moderate sleet showers    20	Heavy rain              27	Heavy snowfall

  const WeaterForcastTemperature = ({ weatherData }: WeaterForcastTemperatureProps) => {
    const degrees = weatherData?.find((item) => item.name === WeatherPropertyCodes.t)?.values[0];
    const weatherSymbolData = weatherData?.find((item) => item.name === WeatherPropertyCodes.Wsymb2);

    const weatherSymbolNumber = Number(weatherSymbolData?.values[0]);
    let weatherSymbol = '/Assets/icons/weather/halfclearsky.png';
    switch (weatherSymbolNumber) {
      case 1:
        weatherSymbol = '/Assets/icons/weather/sunny.png';
        break;
      case 2:
      case 3:
      case 4:
        weatherSymbol = '/Assets/icons/weather/halfclearsky.png';
        break;
      case 5:
      case 6:
        weatherSymbol = '/Assets/icons/weather/cloudy.png';
        break;
      case 7:
        weatherSymbol = '/Assets/icons/weather/foggy.png';
        break;
      case 8:
      case 9:
      case 10:
      case 18:
      case 19:
      case 20:
        weatherSymbol = '/Assets/icons/weather/rainy.png';
        break;
      case 11:
      case 21:
        weatherSymbol = '/Assets/icons/weather/thunderstorm.png';
        break;
      case 12:
      case 13:
      case 14:
      case 22:
      case 23:
      case 24:
        weatherSymbol = '/Assets/icons/weather/sleet.png';
        break;
      case 15:
      case 16:
      case 17:
      case 25:
      case 26:
      case 27:
        weatherSymbol = '/Assets/icons/weather/snowfall.png';
        break;
      default:
        weatherSymbol = '/Assets/icons/weather/halfclearsky.png';
        break;
    }

    return (
      <div className="forcast-temperature">
        <img className="forcast-temperature-symbol" alt="weather-symbol" src={weatherSymbol} />
        <div className="forcast-temperature-degrees">{`${degrees}°`}</div>
      </div>
    );
  };

  // --------------------------------------
  //  WeaterForcastDetails
  // --------------------------------------

  const WeaterForcastDetails = ({ weatherData }: WeaterForcastDetailsProps) => {
    const windStrength = weatherData?.find((item) => item.name === WeatherPropertyCodes.ws)?.values[0];
    const windGustStrength = weatherData?.find((item) => item.name === WeatherPropertyCodes.gust)?.values[0];
    const visibilty = weatherData?.find((item) => item.name === WeatherPropertyCodes.vis)?.values[0];

    return (
      <div className="weather-block-forcast-details">
        <div className="forcast-details-item">
          <div className="forcast-details-item-name">{t('weather.gust')}</div>
          <div className="forcast-details-item-value">{`${windGustStrength} m/s`}</div>
        </div>
        <div className="forcast-details-item">
          <div className="forcast-details-item-name">{t('weather.wind')}</div>
          <div className="forcast-details-item-value">{`${windStrength} m/s`}</div>
        </div>
        <div className="forcast-details-item">
          <div className="forcast-details-item-name">{t('weather.visibility')}</div>
          <div className="forcast-details-item-value">{`${visibilty} km`}</div>
        </div>
      </div>
    );
  };

  // ----------------------------------------------------------------------------
  //  useEffect - arrivalDepartureData
  // ----------------------------------------------------------------------------

  useEffect(() => {
    if (weatherDataItem) {
      setRefreshData(false);
    }
  }, [weatherDataItem]);

  const navigateToUrl = () => {
    if (url && target) {
      window.open(url, target);
    }
  };

  // ----------------------------------------------------------------------------
  //  JSX
  // ----------------------------------------------------------------------------

  return (
    <div
      ref={ref}
      className={`weather-block ${linkUrl ? 'clickable' : ''}`}
      onClick={navigateToUrl}
      onKeyDown={(e) => (e.key === 'Enter' ? navigateToUrl() : null)}
      tabIndex={linkUrl ? 0 : -1}
    >
      <div className={`weather-block-content ${weatherDataItem ? 'show' : ''}`}>
        <div className="weather-block-location-text">
          <img src="/Assets/icons/small_double_chevrons_white.svg" alt="location header chevrons" />
          <h3 className="weather-block-location-name">{locationName}</h3>
        </div>
        <div className="temperature-wind">
          <WeaterForcastTemperature weatherData={weatherDataItem} />
          <WeaterForcastWindDirection weatherData={weatherDataItem} show={!!weatherDataItem} />
        </div>
        <WeaterForcastDetails weatherData={weatherDataItem} />
      </div>

      <div
        className={`weather-block-overlay ${weatherDataItem ? 'show' : ''}`}
        style={{ background: 'linear-gradient(110deg, #103558 70%, #00000000 70%)' }}
      ></div>
      <BackgroundImage url="/Assets/images/weather_background.jpg" />
      <div className="smhi">Källa: SMHI</div>
    </div>
  );
};

export default WeatherBlock;
